<script setup>
import { useDateFormat } from '@vueuse/core';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const { getFormattedDates } = useDateTranslation();
const formattedDurationDates = computed(() => {
  if (props.data?.duration) {
    return getFormattedDates(props.data?.duration);
  }
});

const formattedDates = computed(() => {
  return useDateFormat(props.data?.published, 'DD.MM.YYYY');
});
</script>
<template>
  <section
    :id="data.id"
    class="section section--hero-article bg-gray"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="hero-article">
        <div
          v-if="data.image"
          class="hero-article__media"
          data-aos="zoom-in"
          data-aos-easing="ease-out-quad"
        >
          <NuxtImg
            v-if="data?.image?.src"
            :src="data.image.src"
            :alt="data.image.alt"
            :height="data.image.height"
            :width="data.image.width"
            class="hero-article__image"
            loading="lazy"
          />
        </div>
        <h1
          v-if="data.title"
          class="mb-0 hero-article__title h1"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h1>
        <div
          class="hero-article__info body-3-regular"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <div
            v-if="data.intro"
            class="hero-article__description"
            v-html="data.intro"
          ></div>
          <div
            v-if="
              data?.duration &&
              (data?.duration.start || data?.duration.end) &&
              formattedDurationDates
            "
            class="hero-article__duration body-3-regular"
          >
            {{ $t('promotion.duration_start') }}
            <span
              v-if="
                formattedDurationDates?.startDate ||
                formattedDurationDates?.endDate
              "
              class="hero-article__duration-time"
            >
              <template v-if="formattedDurationDates.startDate">
                {{ $t('promotion.duration_from') }}
                {{ formattedDurationDates.startDate }}
              </template>
              <template v-if="formattedDurationDates.endDate">
                {{ $t('promotion.duration_between') }}
                {{ formattedDurationDates.endDate }}
              </template>
            </span>
          </div>
          <span v-if="data.published" class="hero-article__date">{{ formattedDates }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.hero-article {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'media' 'title' 'info';
  gap: 32px;
  @include media-breakpoint-up(sm) {
    gap: 48px;
  }
  @include media-breakpoint-up(md) {
    display: grid;
    grid-template-areas: 'title media' 'info media';
    grid-template-columns: 6fr 6fr;
    gap: 16px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__title {
    margin: 0;
    grid-area: title;
    text-align: center;
    @include media-breakpoint-up(md) {
      text-align: left;
    }
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 24px;
    grid-area: info;
    text-align: center;
    &:deep(p:last-child) {
      margin: 0;
    }
    @include media-breakpoint-up(sm) {
      gap: 40px;
      margin: 0 auto;
      max-width: 52.8%;
    }
    @include media-breakpoint-up(md) {
      margin: auto 0 0 0;
      max-width: 65.8%;
      text-align: left;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__media {
    grid-area: media;
    margin: 0 auto;
    width: 100%;
    @include media-breakpoint-up(sm) {
      max-width: 53.4%;
    }
    @include media-breakpoint-up(md) {
      height: 100%;
      max-width: unset;
      padding-left: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-left, 48);
    }
  }
  &__image {
    aspect-ratio: 328/328;
    border-radius: 8px;
    height: 100%;
    object-fit: cover;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
  }
}
</style>
